"use client"

import Button from "components/Buttons/Button"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { CustomEase } from "gsap/all"
import KickerCreditCard from "images/Icons/KickerCredit.inline.svg"
import card1 from "images/home/hero/Card1.png"
import card2 from "images/home/hero/Card2.png"
import card3 from "images/home/hero/Card3.png"
import card4 from "images/home/hero/Card4.png"
import { InfiniteSideScroll } from "library/InfiniteSideScroll"
import StaticImage from "library/StaticImage"
import { css, fmobile, fresponsive, ftablet, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { getMedia, useMedia } from "library/useMedia"
import { getResponsivePixels } from "library/viewportUtils"
import { useRef } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

gsap.registerPlugin(ScrollTrigger, CustomEase)

const MARQUEE_TEXT = [
	"Unpredicatable rate increases",
	"Lack of Transparency",
	"Implementation headaches",
	"claim denials",
	"frustrated users",
]

const CarouselImages = [card1, card2, card3, card4]

export default function Hero() {
	const carouselRef = useRef<HTMLDivElement | null>(null)
	const sideScroll = useRef<HTMLDivElement | null>(null)
	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const mobile = useMedia(false, false, false, true)
	const heightTime = useMedia(3, 3, 3, 0)

	useAnimation(
		() => {
			if (!carouselRef.current) return

			const carousel = carouselRef.current
			const imageElements = Array.from(carousel.children)
			const totalImages = imageElements.length

			if (totalImages < 3) return

			const positions = {
				offscreenIn: {
					transform: getMedia(
						"translateX(-200%)",
						"translateX(-200%)",
						"translateY(250%) translateX(0)",
						"translateX(-200%)",
					),
					left: getMedia(
						"auto",
						"auto",
						`${getResponsivePixels(81.4)}`,
						"auto",
					),
					top: getMedia("auto", "auto", "50%", "auto"),
				},
				visibleIn: {
					transform: getMedia(
						"translateX(-84%)",
						"translateX(-84%)",
						"translateY(79%) translateX(0)",
						"translateX(-84%)",
					),
					left: getMedia(
						"auto",
						"auto",
						`${getResponsivePixels(81.4)}`,
						"auto",
					),
					top: getMedia("auto", "auto", "50%", "auto"),
				},
				visibleMiddle: {
					transform: getMedia(
						"translateX(-50%) scale(1.2)",
						"translateX(-50%) scale(1.2)",
						"translate(0, -50%) scale(1.2)",
						"translateX(-50%) scale(1.2)",
					),
					left: getMedia("50%", "50%", `${getResponsivePixels(81.4)}`, "50%"),
					top: getMedia("auto", "auto", "50%", "auto"),
				},
				visibleOut: {
					transform: getMedia(
						"translateX(157%)",
						"translateX(157%)",
						"translateY(-179%) translateX(0)",
						"translateX(149%)",
					),
					left: getMedia(
						"auto",
						"auto",
						`${getResponsivePixels(81.4)}`,
						"auto",
					),
					top: getMedia("auto", "auto", "50%", "auto"),
				},
				offscreenOut: {
					transform: getMedia(
						"translateX(250%)",
						"translateX(250%)",
						"translateY(-280%) translateX(0)",
						"translateX(250%)",
					),
					left: getMedia(
						"auto",
						"auto",
						`${getResponsivePixels(81.4)}`,
						"auto",
					),
					top: getMedia("auto", "auto", "50%", "auto"),
				},
			}

			interface CarouselItem {
				element: HTMLElement
				position: keyof typeof positions
			}

			const carouselItems: CarouselItem[] = imageElements.map(
				(element, index) => {
					let position: keyof typeof positions

					if (index === 0) position = "visibleOut"
					else if (index === 1) position = "visibleMiddle"
					else if (index === 2) position = "visibleIn"
					else position = "offscreenIn"

					return { element: element as HTMLElement, position }
				},
			)

			const mainTl = gsap.timeline()

			const createCycle = (): gsap.core.Timeline => {
				const cycleTl = gsap.timeline({
					onComplete: () => {
						const firstItem = carouselItems.shift()
						if (firstItem) {
							firstItem.position = "offscreenIn"
							carouselItems.push(firstItem)
							gsap.set(firstItem.element, {
								transform: positions[firstItem.position].transform,
								left: positions[firstItem.position].left,
								top: positions[firstItem.position].top,
							})
						}

						gsap.delayedCall(3, () => {
							mainTl.add(createCycle())
						})
					},
				})

				for (const item of carouselItems) {
					let targetPosition: keyof typeof positions

					switch (item.position) {
						case "visibleOut":
							targetPosition = "offscreenOut"
							break
						case "visibleMiddle":
							targetPosition = "visibleOut"
							break
						case "visibleIn":
							targetPosition = "visibleMiddle"
							break
						case "offscreenIn":
							if (item === carouselItems[3]) {
								targetPosition = "visibleIn"
							} else {
								continue
							}
							break
						default:
							continue
					}

					cycleTl.to(
						item.element,
						{
							transform: positions[targetPosition].transform,
							left: positions[targetPosition].left,
							top: positions[targetPosition].top,
							duration: 1.5,
							ease: "power3.inOut",
							onComplete: () => {
								item.position = targetPosition
							},
						},
						0,
					)
				}

				return cycleTl
			}

			mainTl.add(createCycle())
		},
		[],
		{ recreateOnResize: true, scope: wrapperRef },
	)

	useAnimation(
		() => {
			const tl = gsap.timeline({
				delay: 2.87,
			})

			const myEase = CustomEase.create(
				"custom",
				"M0,0 C0.37,0 0.827,-0.102 0.917,0.693 0.95,0.994 1,1 1,1 ",
			)

			const animationTime = 2

			if (mobile) {
				tl.to(
					sideScroll.current,
					{
						marginTop: 0,
						y: 20,
						duration: animationTime,
						ease: myEase,
						onComplete: () => {
							gsap.set(sideScroll.current, {
								opacity: 0,
							})
						},
					},
					0,
				)
			}

			tl.to(
				sideScroll.current,
				{
					width: 0,
					duration: heightTime === 0 ? 2 : animationTime,
					ease: myEase,
				},
				0,
			)
			tl.to(
				sideScroll.current,
				{
					height: 0,
					duration: heightTime === 0 ? 2 : 1,
					ease: myEase,
				},
				heightTime === 0 ? 0 : 1,
			)

			tl.to(
				".hero-green",
				{
					skewX: "-10deg",
					color: colors.js.green200,
					duration: 0.4,
					ease: "back.out(2)",
				},
				animationTime - 0.1,
			)
		},
		[heightTime, mobile],
		{ scope: wrapperRef },
	)

	return (
		<Wrapper ref={wrapperRef}>
			<InnerWrapper>
				<TitleWrapper>
					<DesktopTitle>
						Health insurance that
						<GreenText className="hero-green">&nbsp;doesn't</GreenText>
					</DesktopTitle>
					<MobileTitle> Health Insurance</MobileTitle>
					<Title2>
						<DesktopGreenText className="hero-green hero-shrink">
							get in
						</DesktopGreenText>
						<MobileTitle className="hero-shrink">
							that{" "}
							<MobileGreenText className="hero-green">
								doesn't get
							</MobileGreenText>
						</MobileTitle>
						<ScrollWrapper ref={sideScroll}>
							<InfiniteSideScroll marqueeSpeed={1.2}>
								{MARQUEE_TEXT.map((text) => (
									<MarqueeText key={text}>
										{text}
										<MarqueeIcon>
											<KickerCreditCard />
										</MarqueeIcon>
									</MarqueeText>
								))}
							</InfiniteSideScroll>
						</ScrollWrapper>
						<DesktopGreenText className="hero-green hero-shrink">
							the way.
						</DesktopGreenText>
						<BelowScrollerText className="hero-green hero-shrink">
							in the way.
						</BelowScrollerText>
					</Title2>
				</TitleWrapper>

				<BottomSection>
					<HeroCTA>
						<Text>
							Join hundreds of companies who choose Arlo for health insurance
							that actually works—transparent costs, prevention-first benefits,
							zero red tape. Healthier employees, stronger financials.
						</Text>
						<Button size="large" variant="light" href={links.contact}>
							Get a Custom Quote Today
						</Button>
					</HeroCTA>

					<Carousel ref={carouselRef}>
						{CarouselImages.map((image) => (
							<ImageWrapper key={image.src}>
								<StaticImage
									width={493}
									height={244}
									src={image.src}
									alt="carousel-image"
									loading="eager"
								/>
							</ImageWrapper>
						))}
					</Carousel>
				</BottomSection>
			</InnerWrapper>
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...fresponsive(css`
		width: 100%;
		display: grid;
		place-items: center;
		padding: 0;
		background: ${colors.offWhite};
	`),
})

const InnerWrapper = styled("div", {
	...fresponsive(css`
		width: 1440px;
		height: 800px;
		padding: 84px 32px 32px;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		gap: 100px;
	`),

	...ftablet(css`
		width: 100%;
		height: calc(100lvh - 72px);
		padding: 108px 32px 32px;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		gap: 175px;
	`),

	...fmobile(css`
		width: 100%;
		height: auto;
		padding: 64px 16px 16px;
		gap: 66px;
		align-items: center;
	`),
})

const DesktopTitle = styled("div", {
	...fresponsive(css`
		width: 100%;
		color: ${colors.green500};
		${textStyles.custom.h1};
		display: flex;
	`),

	...ftablet(css`
		${textStyles.h1};
		display: inline;
		width: 100%;
		text-align: center;
		margin-bottom: 10px;

		&.green-text {
			display: inline;
		}
	`),

	...fmobile(css`
		display: none;
	`),
})

const MobileTitle = styled("div", {
	...fresponsive(css`
		color: ${colors.green500};
		display: none;
	`),

	...fmobile(css`
		${textStyles.h5};
		display: inline-block;
		width: 100%;
		text-align: center;
		margin-bottom: 24px;

		&.green-text {
			display: inline;
		}
	`),
})

const Title2 = styled("div", {
	...fresponsive(css`
		width: fit-content;
		color: ${colors.green500};
		text-align: center;
		${textStyles.h1};
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		margin: 0 auto;

		.hero-shrink {
			margin: 0 8px;
		}
	`),

	...ftablet(css`
		width: 960px;
		white-space: nowrap;
		flex-wrap: nowrap;
	`),

	...fmobile(css`
		${textStyles.h5};
		width: 343px;
		gap: 8px;
	`),
})

const GreenText = styled("span", {
	...fresponsive(css`
		color: ${colors.green500};
	`),
})

const DesktopGreenText = styled("span", {
	...fresponsive(css`
		color: ${colors.green500};
	`),

	...fmobile(css`
		display: none;
	`),
})

const MobileGreenText = styled("span", {
	...fresponsive(css`
		display: none;
		color: ${colors.green500};
	`),

	...fmobile(css`
		display: inline-block;
		width: fit-content;
	`),
})

const BelowScrollerText = styled("div", {
	...fresponsive(css`
		display: none; /* Hide on desktop by default */
	`),

	...fmobile(css`
		display: block;
		width: 327px;
		height: 32px;
		text-align: center;
		color: ${colors.green500};
		${textStyles.h5};
		margin-top: 16px;
		padding: 0 16px;
		position: relative;
		z-index: 1;
	`),
})

const MarqueeText = styled("div", {
	...fresponsive(css`
		${textStyles.titleS}
		color: ${colors.orange};
	`),
})

const MarqueeIcon = styled("div", {
	...fresponsive(css`
		position: relative;
		display: inline-block;
		width: 18px;
		height: 11px;
		margin: 0 30px;

		svg {
			width: 18px;
			height: 11px;
			opacity: 0.5;
		}
	`),
})

const ScrollWrapper = styled("div", {
	...fresponsive(css`
		position: relative;
		width: 662px;
		height: 79px;
		border: 1px solid ${colors.green100};
		border-radius: 12px;
		display: grid;
		overflow: clip;
		place-items: center;
		transform: translateY(20px);

		.track {
			padding: 20px 0;
			margin: -20px 0;
		}
	`),

	...ftablet(css`
		width: 307px;
	`),

	...fmobile(css`
		width: 327px;
		height: 62px;
		transform: translateY(10px);
		margin: 10px 0;
	`),
})

const TitleWrapper = styled("div", {
	...fresponsive(css`
		width: 100%;
		height: 193px;
		display: flex;
		flex-direction: column;
	`),

	...ftablet(css`
		height: 288px;
	`),

	...fmobile(css`
		height: 228px;
		justify-content: center;
	`),
})

const BottomSection = styled("div", {
	...fresponsive(css`
		width: 100%;
		display: flex;
		height: 328px;
	`),

	...ftablet(css`
		height: 600px;
	`),

	...fmobile(css`
		height: 360px;
		flex-direction: column-reverse;
		width: 327px;
	`),
})

const HeroCTA = styled("div", {
	...fresponsive(css`
		display: flex;
		padding: 32px 157px 32px 32px;
		flex-direction: column;
		justify-content: space-between;
		align-items: flex-start;
		width: 689px;
		border: 1px solid ${colors.green100};
		height: 100%;
	`),

	...ftablet(css`
		padding: 32px;
		width: 403px;
	`),

	...fmobile(css`
		padding: 20px;
		width: 100%;
		margin: 0 auto;
	`),
})

const Text = styled("div", {
	...fresponsive(css`
		${textStyles.bodyL};
		color: ${colors.green500};
	`),

	...fmobile(css`
		${textStyles.bodyXS};
	`),
})

const ImageWrapper = styled("div", {
	...fresponsive(css`
		position: absolute;
		width: 394.4px;
		height: 195.2px;
		transform: translateX(-200%);
		transform-origin: center center;

		:nth-of-type(1) {
			transform: translateX(157%);
		}

		:nth-of-type(2) {
			transform: translateX(-50%) scale(1.2);
			left: 50%;
		}

		:nth-of-type(3) {
			transform: translateX(-84%);
		}
	`),

	...ftablet(css`
		transform: translateX(0);
		left: 81.4px;
		top: 200%;
		opacity: 1;

		:nth-of-type(1) {
			top: 50%;
			transform: translateY(79%);
			opacity: 1;
		}

		:nth-of-type(2) {
			left: 81.4px;
			top: 50%;
			transform: translate(0, -50%) scale(1.2);
			opacity: 1;
		}

		:nth-of-type(3) {
			top: 50%;
			transform: translateY(-179%);
			opacity: 1;
		}
	`),

	...fmobile(css`
		position: absolute;
		width: 197.2px;
		height: 97.6px;
		transform: translateX(-200%);
		transform-origin: center center;

		:nth-of-type(1) {
			transform: translateX(157%);
		}

		:nth-of-type(2) {
			transform: translateX(-50%) scale(1.2);
			left: 50%;
		}

		:nth-of-type(3) {
			transform: translateX(-84%);
		}
	`),
})

const Carousel = styled("div", {
	...fresponsive(css`
		width: 687px;
		height: 100%;
		display: flex;
		gap: 81.4px;
		overflow: clip;
		border: 1px solid ${colors.green100};
		border-left: none;
		align-items: center;
		position: relative;
	`),
	...ftablet(css`
		width: 557px;
	`),

	...fmobile(css`
		width: 100%;
		border-left: 1px solid ${colors.green100};
		border-bottom: none;
	`),
})
